/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  Component,
  ComponentRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UIRouter} from '@uirouter/core';

import {getEditPageComponent, getItemPageComponent} from './route-page-component-registry';
import {readAdminResourceRouteContext} from './admin-resource-route-context';

@Component({
  standalone: false,
  selector: 'adn-admin-resource-item-route-page',
  template: '<ng-template #host></ng-template>'
})
export class AdminResourceItemRoutePageComponent implements OnInit, OnDestroy {
  @Input() item: string;
  /** When set to `edit`, resolves component via getEditPageComponent instead of getItemPageComponent. */
  @Input() variant: 'item' | 'edit' = 'item';
  /** Additional resolved inputs for route-specific editors hosted through this compatibility boundary. */
  @Input() inputOverrides: Record<string, any> = {};

  @ViewChild('host', {read: ViewContainerRef, static: true}) host: ViewContainerRef;

  private componentRef: ComponentRef<any>;

  constructor(private readonly uiRouter: UIRouter) {
  }

  ngOnInit(): void {
    const itemComponent = this.variant === 'edit'
      ? getEditPageComponent(this.item)
      : getItemPageComponent(this.item);
    if (!itemComponent) {
      return;
    }

    const context = readAdminResourceRouteContext(this.uiRouter);
    if (!context.model || !context.modelComms) {
      return;
    }

    this.componentRef = this.host.createComponent(itemComponent);
    this.applyContext(context);
  }

  ngOnDestroy(): void {
    this.componentRef?.destroy();
  }

  private applyContext(context: ReturnType<typeof readAdminResourceRouteContext>): void {
    this.componentRef.setInput('model', context.model);
    this.componentRef.setInput('modelComms', context.modelComms);
    this.componentRef.setInput('isNew', context.isNew);
    if (context.readOnly !== undefined && 'readOnly' in this.componentRef.instance) {
      this.componentRef.setInput('readOnly', context.readOnly);
    }
    Object.entries(this.inputOverrides || {}).forEach(([key, value]) => this.componentRef.setInput(key, value));
  }
}
